import { toCssHex, type Rgba } from '../color.ts';
import { FITTINGS, type BoatFittings } from './fittings.ts';
import type { BoatDocument, SideViewColors, SideViewPartKey } from './types.ts';

/** Authored `.boat-keel-fill`. A v1 file never chose it, so migrate keeps this grey. */
export const FACTORY_SIDE_KEEL: Rgba = { r: 0xb3, g: 0xb3, b: 0xb3, a: 1 };

/**
 * Fill paths on `boat_side_view.svg`, in paint order.
 * `fromLivery` is the v1 hull / deck / accent slot that used to paint that class.
 * The keel had no slot. Outlines and `.boat-rail` are not parts: they stay black.
 */
export const SIDE_VIEW_PARTS: readonly {
  key: SideViewPartKey;
  className: string;
  label: string;
  fromLivery: 'hull' | 'deck' | 'accent' | null;
}[] = [
  { key: 'roof', className: 'boat-roof-fill', label: 'Roof', fromLivery: 'deck' },
  {
    key: 'hullAccent',
    className: 'boat-hull-accent',
    label: 'Hull accent',
    fromLivery: 'hull',
  },
  {
    key: 'hullBase',
    className: 'boat-hull-base',
    label: 'Hull base',
    fromLivery: 'deck',
  },
  {
    key: 'sideWindow',
    className: 'boat-side-window-fill',
    label: 'Side window',
    fromLivery: 'accent',
  },
  { key: 'wings', className: 'boat-wings-fill', label: 'Wings', fromLivery: 'hull' },
  {
    key: 'rudder',
    className: 'boat-rudder-fill',
    label: 'Rudder',
    fromLivery: 'deck',
  },
  {
    key: 'window',
    className: 'boat-window-fill',
    label: 'Window',
    fromLivery: 'accent',
  },
  { key: 'keel', className: 'boat-keel-fill', label: 'Keel', fromLivery: null },
];

const SIDE_VIEW_FILL_CLASS: ReadonlyMap<string, SideViewPartKey> = new Map(
  SIDE_VIEW_PARTS.map((part) => [part.className, part.key]),
);

/** The colours a v1 hull / deck / accent file already showed, plus the grey keel. */
export function sideViewColorsFromLivery(colors: {
  hull: Rgba;
  deck: Rgba;
  accent: Rgba;
}): SideViewColors {
  const side = {} as SideViewColors;

  for (const part of SIDE_VIEW_PARTS) {
    const source =
      part.fromLivery === null ? FACTORY_SIDE_KEEL : colors[part.fromLivery];
    side[part.key] = { ...source };
  }

  return side;
}

export function cloneSideViewColors(colors: SideViewColors): SideViewColors {
  const copy = {} as SideViewColors;

  for (const part of SIDE_VIEW_PARTS) {
    copy[part.key] = { ...colors[part.key] };
  }

  return copy;
}

/** The drawing is the 7 m skiff: freeboard is length / 7, and the keel is 0.5 m. */
const REFERENCE_LENGTH_M = 7;
const REFERENCE_DRAFT_M = 0.5;

/**
 * `boat2-root` then `boat2-art`. View y grows down; local y grows up.
 * The join is the keel fill's highest local y, where it meets the hull.
 */
const VIEW_SCALE = 1.3333333 * 0.1;
const VIEW_Y_OFFSET = 3206.7331;
const KEEL_JOIN_LOCAL_Y = 16249.337209066862;
const KEEL_JOIN_VIEW_Y = -VIEW_SCALE * KEEL_JOIN_LOCAL_Y + VIEW_Y_OFFSET;

/** Authored frame of `id="boat2-side"`. Width is the boat above the keel. */
const SIDE_VIEW_WIDTH = 3500.2676;
/** Hull outline bottom. The rigid picture ends here; the keel may hang below. */
const ABOVE_KEEL_BOTTOM = 1080.138;
/** Keel group bottom at scale 1, stroke included. */
const KEEL_OUTER_BOTTOM = 1384.925;

function formatSvgNumber(value: number): string {
  const rounded = Math.round(value * 1e6) / 1e6;
  return String(rounded);
}

function cssOpacity(alpha: number): string {
  const clamped = Math.max(0, Math.min(1, Number.isFinite(alpha) ? alpha : 0));
  return String(Math.round(clamped * 1000) / 1000);
}

function classTokens(tag: string): string[] {
  const match = /\bclass="([^"]*)"/.exec(tag);
  if (!match?.[1]) {
    return [];
  }
  return match[1].trim().split(/\s+/);
}

function paintTag(tag: string, hex: string, opacity: string): string {
  let next = tag;
  if (/\bfill-opacity\s*:/.test(next)) {
    next = next.replace(
      /\bfill-opacity\s*:\s*[^;"]+/,
      `fill-opacity:${opacity}`,
    );
  } else if (/\bstyle="/.test(next)) {
    next = next.replace(/\bstyle="/, `style="fill-opacity:${opacity};`);
  }

  if (/\bfill\s*:/.test(next)) {
    next = next.replace(/\bfill\s*:\s*[^;"]+/, `fill:${hex}`);
  } else if (/\bstyle="/.test(next)) {
    next = next.replace(/\bstyle="/, `style="fill:${hex};`);
  } else {
    next = next.replace(/>$/, ` style="fill:${hex};fill-opacity:${opacity}">`);
  }

  return next;
}

/** Sets each side-view fill from its own part colour. Outlines and the rail stay. */
export function paintSideViewSvg(source: string, colors: SideViewColors): string {
  return source.replace(/<[A-Za-z][\w:-]*\b[^>]*>/g, (tag) => {
    const key = classTokens(tag)
      .map((token) => SIDE_VIEW_FILL_CLASS.get(token))
      .find((value) => value !== undefined);
    if (!key) {
      return tag;
    }
    const color = colors[key];
    return paintTag(tag, toCssHex(color), cssOpacity(color.a));
  });
}

/**
 * Y scale for `g.boat-keel` about the hull join.
 * A deeper draft lengthens the keel. A longer boat, at the same draft,
 * shows a shorter keel against a taller freeboard.
 */
export function sideViewKeelScale(lengthM: number, draftM: number): number {
  if (!(lengthM > 0) || !Number.isFinite(draftM)) {
    return 1;
  }

  const freeboard = lengthM / REFERENCE_LENGTH_M;
  const authoredFreeboard = REFERENCE_LENGTH_M / REFERENCE_LENGTH_M;
  return draftM / freeboard / (REFERENCE_DRAFT_M / authoredFreeboard);
}

function sideViewHeight(scale: number): number {
  const keelBottom =
    KEEL_JOIN_VIEW_Y + (KEEL_OUTER_BOTTOM - KEEL_JOIN_VIEW_Y) * scale;
  return Math.max(ABOVE_KEEL_BOTTOM, keelBottom);
}

function hideOffFittings(source: string, fittings: BoatFittings): string {
  return source.replace(/<g\b[^>]*>/g, (tag) => {
    const tokens = classTokens(tag);
    const hidden = FITTINGS.some(
      (fitting) => !fittings[fitting.key] && tokens.includes(fitting.groupClass),
    );

    if (!hidden) {
      return tag;
    }

    if (/\bdisplay\s*:/.test(tag)) {
      return tag.replace(/\bdisplay\s*:\s*[^;"]+/, 'display:none');
    }

    if (/\bstyle="/.test(tag)) {
      return tag.replace(/\bstyle="/, 'style="display:none;');
    }

    return tag.replace(/^<g\b/, '<g style="display:none"');
  });
}

function withKeelScale(source: string, scale: number): string {
  const y = formatSvgNumber(KEEL_JOIN_LOCAL_Y);
  const transform = `translate(0,${y}) scale(1,${formatSvgNumber(scale)}) translate(0,${formatSvgNumber(-KEEL_JOIN_LOCAL_Y)})`;

  return source.replace(/<g\b[^>]*>/g, (tag) => {
    if (!classTokens(tag).includes('boat-keel')) {
      return tag;
    }
    if (/\btransform="/.test(tag)) {
      return tag.replace(/\btransform="[^"]*"/, `transform="${transform}"`);
    }
    return tag.replace(/^<g\b/, `<g transform="${transform}"`);
  });
}

/**
 * Inline side view for the workshop card.
 * The boat above the keel keeps its aspect and fits the card width.
 * Only the keel scales, and the view grows to include it.
 */
export function renderSideViewSvg(
  source: string,
  boat: Pick<BoatDocument, 'lengthM' | 'draftM' | 'side' | 'fittings'>,
): string {
  const scale = sideViewKeelScale(boat.lengthM, boat.draftM);
  const width = formatSvgNumber(SIDE_VIEW_WIDTH);
  const height = formatSvgNumber(sideViewHeight(scale));
  const waterline = formatSvgNumber(KEEL_JOIN_VIEW_Y);
  const painted = withKeelScale(
    hideOffFittings(
      paintSideViewSvg(source, boat.side).replace(
        /^\uFEFF?\s*<\?xml[\s\S]*?\?>\s*/,
        '',
      ),
      boat.fittings,
    ),
    scale,
  );

  return painted.replace(/<svg\b[^>]*>/, (tag) => {
    const opened = tag
      .replace(/\swidth="[^"]*"/, '')
      .replace(/\sheight="[^"]*"/, '')
      .replace(/\sviewBox="[^"]*"/, '');
    return `${opened.slice(0, -1)} class="boat-card-profile-svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" aria-hidden="true"><rect class="boat-card-sea" x="0" y="0" width="${width}" height="${height}" /><line class="boat-card-waterline" x1="0" y1="${waterline}" x2="${width}" y2="${waterline}" />`;
  });
}
